import { css } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { IIllustratedText, IIllustratedTextFields } from '../../types/@types/generated/contentful';
import { breakpoints, colors, spacing } from '../../utils/styleguide';
import GhostButton from '../component-library/buttons/GhostButton';
import PrimaryButton from '../component-library/buttons/PrimaryButton';
import LayoutHorizontalListSection from '../component-library/layout/HorizontalListSection/HorizontalListLayout';
import SectionBackground from '../component-library/SectionBackground';
import SimpleModal from '../component-library/SimpleModal';
import Typography from '../component-library/text/Typography';
import Image, { ImageStyleProps } from './Image';
import { PageSectionComponent } from './pageSections';
import RichText from './RichText/RichText';
import buttonToProps from './util/buttonToCompatibleProps';
import orientedMediaToCompatibleProps from './util/orientedMediaToCompatibleProps';
import sectionToCompatibleProps from './util/sectionToCompatibleProps';

type ModalContentProps = {
  readMore: IIllustratedTextFields;
  setReadMore: React.Dispatch<React.SetStateAction<IIllustratedTextFields | undefined>>;
};

const ModalContent = ({ readMore, setReadMore }: ModalContentProps) => {
  const { image } = orientedMediaToCompatibleProps({
    orientedMedia: readMore.media,
    image: readMore.image,
  });
  return (
    <>
      {image && (
        <Image
          wrapperCss={css`
            max-width: 40px;
            max-height: 40px;
          `}
          css={css`
            height: 100%;
            width: 100%;
            object-fit: contain;
          `}
          asset={image}
        />
      )}
      <Typography
        css={css`
          margin-top: ${spacing[3]}px;
        `}
      >
        {readMore.readMoreText}
      </Typography>
      <PrimaryButton
        css={css`
            width: 100%;
            margin-top ${spacing[6]}px;
            margin-bottom ${spacing[4]}px;
          `}
        size="small"
        label={readMore.readMoreCloseButton?.fields.label || 'Got it'}
        onClick={() => setReadMore(undefined)}
      />
    </>
  );
};

const titleImageStyle = css`
  border-radius: 4px;
`;

const logoStyle = css`
  height: 48px;
  width: auto;
  max-width: 130px;
  object-fit: contain;
  object-position: left;
  ${breakpoints.desktop} {
    height: 40px;
  }
`;

const HorizontalListSection: PageSectionComponent<null> = ({ section }) => {
  const [readMore, setReadMore] = useState<IIllustratedTextFields>();
  const { title, backgroundColor, id: sectionId } = sectionToCompatibleProps({ section });
  const titleFunc = (props: { color: string }) => (
    <RichText document={title} textColor={props.color} centered />
  );
  const { settings, references } = section.fields;

  const { maxMediaWidth: deprecated_maxMediaWidth, withIcons: deprecated_withIcons } =
    settings || {};

  const sections = references as unknown as IIllustratedText[];

  const Title = title && titleFunc;
  const modalContent = useMemo(() => {
    if (readMore) return <ModalContent readMore={readMore} setReadMore={setReadMore} />;
    return undefined;
  }, [readMore, setReadMore]);

  return (
    <SectionBackground id={sectionId} backgroundColor={backgroundColor}>
      <SimpleModal
        open={!!modalContent}
        onClose={() => setReadMore(undefined)}
        body={modalContent}
        hideToolbar
        padding={spacing[4]}
      />
      <LayoutHorizontalListSection
        Title={Title}
        deprecated_withIcons={deprecated_withIcons}
        sections={sections?.map(({ fields }) => {
          const { image, maxWidth } = orientedMediaToCompatibleProps({
            orientedMedia: fields.media,
            image: fields.image,
          });
          const { label, href } = buttonToProps({
            button: fields.button,
            settings: fields.settings,
          });

          const handleLinkClick = (fields: IIllustratedTextFields) => {
            setReadMore(fields);
          };
          return {
            title: (fields.title && <RichText document={fields.title} />) || '',
            content:
              (fields.description && (
                <RichText textColor={colors.darkGrey} document={fields.description} />
              )) ||
              '',
            Image:
              image &&
              ((props: ImageStyleProps) => (
                <Image
                  asset={image}
                  {...props}
                  css={css`
                    ${titleImageStyle};
                    ${props.css}
                  `}
                />
              )),
            Logos: fields?.logos?.map((image) => (
              <Image key={image.sys.id} asset={image} css={logoStyle} />
            )),
            Button:
              href || fields.readMoreText ? (
                <GhostButton
                  href={!fields.readMoreText ? href : undefined}
                  onClick={fields.readMoreText ? () => handleLinkClick(fields) : undefined}
                  label={label || 'Learn more'}
                  color={colors.blueDark}
                  hideUnderline
                  trailingIconName="arrow-long-right"
                />
              ) : undefined,
            isIcon: fields.isIcon,
            maxMediaWidth: maxWidth || deprecated_maxMediaWidth,
          };
        })}
      />
    </SectionBackground>
  );
};

export default HorizontalListSection;
